///@ts-nocheck
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Checkbox,
  TextField,
  FormControlLabel,
  Box,
  Chip,
  Autocomplete,
  Popper,
} from "@mui/material";

import { post } from "requests";
import CustomModal from "components/CustomModal";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDButtonRoot from "components/MDButton/MDButtonRoot";

interface CheckboxState {
  id: string;
  value: string;
  checked: boolean;
}

interface User {
  name: string;
  email: string;
}

interface SendEmailComponentProps {
  dealApplicationId: any;
  isGeneralEmail: boolean | undefined;
  showModal: boolean;
  onClose: () => void;
}

const SendEmailComponent: React.FC<SendEmailComponentProps> = ({
  showModal,
  onClose,
  dealApplicationId,
  selectedDocuments,
  isGeneralEmail,
}) => {
  const [checkboxes, setCheckboxes] = useState<CheckboxState[]>([
    { id: "funders", value: "Funder", checked: true },
    { id: "merchantOwners", value: "Merchant Owner(s)", checked: true },
    { id: "salesrep", value: "Representative", checked: true },
    { id: "isobroker", value: "ISO/Broker", checked: true },
  ]);
  const [finalEmailRecipients, setFinalEmailRecipients] = useState<User[]>([]);
  const [emailUsers, setEmailUsers] = useState<Record<string, User[]>>({});
  const [newEmail, setNewEmail] = useState("");
  const [additionalDropdownUsers, setAdditionalDropdownUsers] = useState<User[]>([]);
  const [dropDownSelectedUsers, setDropdownSelectedUsers] = useState<User[]>([]);
  const [emailSubject, setEmailSubject] = useState<string>("");
  const [emailBody, setEmailBody] = useState<string>("");

  useEffect(() => {
    fetchRecipientsByCategory();
    if (isGeneralEmail) {
      fetchEmailData();
    }
  }, []);

  useEffect(() => {
    const filteredList = dropDownSelectedUsers.filter((user) => {
      const email = user && user.value && user.value.email;
      return !finalEmailRecipients.some((recipient) => recipient.email === email);
    });
    setDropdownSelectedUsers(filteredList);
  }, [finalEmailRecipients]);

  useEffect(() => {
    checkboxes.forEach((checkbox, index) => {
      handleCheckBoxChange(index);
    });
  }, [additionalDropdownUsers]);

  const handleSendGenericEmail = async () => {
    const selectedFileIds = [];
    const dropDownUsers = dropDownSelectedUsers.map((user) => user && user.value);
    const recipients = [...finalEmailRecipients, ...dropDownUsers];

    if (recipients.length > 0) {
      selectedDocuments.map((item) => {
        selectedFileIds.push(item.id);
      });
      let params = {
        toMailId: finalEmailRecipients,
        selectedFileIds: selectedFileIds,
        subject: emailSubject,
        emailBody: emailBody,
        merchant: "",
      };

      const response = await post("sendGenericEmail", params);
    }
    onClose();
  };

  const fetchRecipientsByCategory = async () => {
    const responseData = await post("fetchRecipientsByCategory", {
      applicationId: dealApplicationId,
    });

    setEmailUsers(responseData);
    setAdditionalDropdownUsers(responseData.users?.filter((usr: User) => usr.email) || []);
    setFinalEmailRecipients(responseData?.defaultRecipients || []);
  };

  const fetchEmailData = async () => {
    const responseData = await post("getGeneralEmailTemplate", {
      dealApplicationId: dealApplicationId,
      topicName: topicName,
    });
  };

  const handleCheckBoxChange = (index) => {
    const updatedCheckboxes = [...checkboxes];
    updatedCheckboxes[index].checked = !updatedCheckboxes[index].checked;
    setCheckboxes(updatedCheckboxes);
    populateEmailSenders(updatedCheckboxes[index].id);
  };

  const populateEmailSenders = (id) => {
    let recipients = [];
    let recipientsRemoved = [];
    const checkedBox = checkboxes.filter((box) => box.id === id)[0];
    if (checkedBox.checked) {
      const addedUsers = emailUsers && emailUsers[id];
      if (addedUsers) {
        recipients = [...recipients, ...addedUsers.filter((user) => user.email)];
      }
    } else {
      const removedUsers = emailUsers && emailUsers[id];
      if (removedUsers) {
        const checkedUsers = checkboxes
          .filter((box) => box.checked || box.id === id)
          .flatMap((box) => emailUsers[box.id]);
        removedUsers.forEach((eachUser) => {
          const isEmailInList = checkedUsers.some((usr) => usr.email === eachUser.email);
          const isDefaultUser =
            emailUsers &&
            emailUsers.defaultRecipients &&
            emailUsers.defaultRecipients.some((user) => user.email == eachUser.email);
          if (isEmailInList && !isDefaultUser) {
            recipientsRemoved.push(eachUser);
          }
        });
      }
    }
    setFinalEmailRecipients((prevState) => {
      const mergedList = [...prevState, ...recipients].filter(
        (recipient) =>
          !recipientsRemoved.some((removedUser) => removedUser.email === recipient.email)
      );
      const uniqueSet = new Set(mergedList.map((user) => JSON.stringify(user)));
      return Array.from(uniqueSet).map((entry) => JSON.parse(entry));
    });
  };

  const handleAddEmail = useCallback(() => {
    if (!isValidEmail(newEmail)) {
      alert("Please enter a valid email address.");
      return;
    }

    const isDuplicate = finalEmailRecipients.some(
      (recipient) => recipient.email === newEmail.trim()
    );
    if (isDuplicate) {
      alert("This email is already added.");
      return;
    }

    setFinalEmailRecipients((prevState) => [...prevState, { name: "", email: newEmail.trim() }]);
    setNewEmail("");
  }, [newEmail, finalEmailRecipients]);

  const isValidEmail = (email: string) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  };

  const handleRemoveUser = useCallback((index: number) => {
    setFinalEmailRecipients((prevState) => prevState.filter((_, i) => i !== index));
  }, []);

  const handleRemoveDropDownUser = useCallback((index: number) => {
    setDropdownSelectedUsers((prevState) => prevState.filter((_, i) => i !== index));
  }, []);

  const customOptions = useMemo(
    () =>
      additionalDropdownUsers
        .filter((usr) => !finalEmailRecipients.some((recipient) => recipient.email === usr.email))
        .map((usr) => ({
          label: `${usr.name} (${usr.email})`,
          value: usr,
        })),
    [additionalDropdownUsers, finalEmailRecipients]
  );

  const CustomPopper = (props: any) => {
    return (
      <Popper
        {...props}
        placement="bottom-end"
        style={{ zIndex: 1300 }}
        modifiers={[
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              rootBoundary: "document",
            },
          },
          {
            name: "flip",
            enabled: false,
          },
        ]}
      />
    );
  };

  return (
    <CustomModal
      open={showModal}
      onClose={onClose}
      title="Send Document"
      cancelText="Cancel"
      submitText="Send"
      onCancel={onClose}
      onSubmit={handleSendGenericEmail}
      maxWidth="md"
      fullWidth={true}
    >
      <MDBox display="flex" flexDirection="column" width="100%" gap={"20px"} marginTop={2}>
        <MDInput
          label="Email Subject"
          value={emailSubject}
          sx={{
            "& .MuiInputLabel-root": {
              fontWeight: 600,
              fontSize: 14,
              letterSpacing: "0.5px",
            },
          }}
          onChange={(e) => setEmailSubject(e.target.value)}
        />

        {!isGeneralEmail && (
          <MDInput
            label="Email Body"
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value)}
            multiline
            rows={3}
            sx={{
              width: "100%",
              resize: "vertical",
              "& .MuiInputLabel-root": {
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: "0.5px",
              },
            }}
          />
        )}
      </MDBox>

      <MDBox sx={{ mt: 2 }}>
        <MDTypography variant="subtitle2" fontWeight={"bold"}>
          NOTIFICATION RECIPIENTS
        </MDTypography>
        <MDBox sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
          {checkboxes.map((checkbox, index) => (
            <FormControlLabel
              key={checkbox.id}
              control={
                <Checkbox checked={checkbox.checked} onChange={() => handleCheckBoxChange(index)} />
              }
              label={checkbox.value}
            />
          ))}
        </MDBox>
      </MDBox>

      <MDBox sx={{ mt: 2 }}>
        <MDTypography variant="subtitle2" fontWeight={"bold"} sx={{ marginBottom: 1 }}>
          Add Recipients
        </MDTypography>
        <Autocomplete
          multiple
          disableCloseOnSelect={true}
          id="search-users"
          options={customOptions}
          getOptionLabel={(option) => `${option.label}`}
          value={dropDownSelectedUsers}
          onChange={(e, newValue) => setDropdownSelectedUsers(newValue as User[])}
          renderInput={(params) => <TextField {...params} label="Search Users" />}
          renderTags={(value, getTagProps) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {value.map((user, index) => (
                <Chip
                  key={user?.value?.email}
                  label={`${user?.value?.name} (${user?.value?.email})`}
                  {...getTagProps({ index })}
                />
              ))}
            </Box>
          )}
          PopperComponent={CustomPopper}
        />
      </MDBox>

      <MDBox sx={{ mt: 2 }}>
        <MDTypography variant="subtitle2" fontWeight={"bold"}>
          Add Email Address
        </MDTypography>
        <MDBox sx={{ display: "flex", gap: 1, mt: 1 }}>
          <TextField
            fullWidth
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Enter email address"
          />
          <MDButton size="small" color={"primary"} variant="contained" onClick={handleAddEmail}>
            <MDTypography fontSize={12} color={"white"}>
              {" "}
              Add
            </MDTypography>
          </MDButton>
        </MDBox>
      </MDBox>

      <MDBox sx={{ mt: 2, mb: 5 }}>
        <MDTypography variant="subtitle2" fontWeight={"bold"}>
          Email will be sent to:
        </MDTypography>
        <MDBox
          sx={{ minHeight: 80, maxHeight: 100, overflow: "auto" }}
          border={1}
          borderColor={"gray"}
        >
          {finalEmailRecipients.map((recipient, index) => (
            <Chip
              key={index}
              label={`${recipient.name} (${recipient.email})`}
              onDelete={() => handleRemoveUser(index)}
              sx={{ m: 0.5 }}
            />
          ))}
          {dropDownSelectedUsers.map((user, index) => (
            <Chip
              key={index}
              label={`${user?.value?.name} (${user?.value?.email})`}
              onDelete={() => handleRemoveDropDownUser(index)}
              sx={{ m: 0.5 }}
            />
          ))}
        </MDBox>
      </MDBox>

      {!isGeneralEmail && (
        <MDBox sx={{ mt: 2, mb: 3 }}>
          <MDTypography variant="subtitle2" fontWeight={"bold"}>
            Files:
          </MDTypography>
          <MDBox
            sx={{ minHeight: 80, maxHeight: 100, overflow: "auto", padding: 1 }}
            border={1}
            borderColor={"gray"}
          >
            {selectedDocuments?.map((doc, key) => (
              <MDTypography key={key}>{doc.name}</MDTypography>
            ))}
          </MDBox>
        </MDBox>
      )}
    </CustomModal>
  );
};

export default SendEmailComponent;
